import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import useDialog, { DialogType } from '@/hooks/useDialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import { putCustomersAPI } from '@/src/apis/rest/customers';
import { Form, FormField } from '@/components/ui/form';
import { useForm, useWatch } from 'react-hook-form';
import { formatter } from '@/src/utils/formatter';
import { TOAST_CONST } from '@/src/constants/toast';
import { PutCustomersRequestBody } from '@/src/apis/rest/customers.dtos';
import * as statusField from '@/src/pages/Customer/statusField';
import { zodResolver } from '@hookform/resolvers/zod';
import FormItem from '@/components/molecule/FormItem';
import React, { useEffect, useMemo } from 'react';
import { CustomersDto } from '@/src/models/CustomersDto';
import useAuthStore from '@/src/store/useAuthStore';
import ConfirmDialog from '@/components/organism/ConfirmDialog';
import { getRecyclingRequestsCheckInprogressAPI } from '@/src/apis/rest/recyclingRequests';

const statusConfirm = {
  normal: {
    title: '일반 회원으로 상태를 변경하시겠어요?',
    content: '',
  },
  withdrawal: {
    title: '회원을 탈퇴 처리하시겠어요?',
    content: '탈퇴 처리할 경우 복구가 불가능해요. 정말 탈퇴 처리하시겠어요?',
  },
  suspended: {
    title: '정지 회원으로 상태를 변경하시겠어요?',
    content: '상태를 변경하면 사용자는 로그인이 불가능해요',
  },
};

interface StatusManageDialogProps {
  customer: CustomersDto | undefined;
  submitCallback?: () => Promise<any>;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

function StatusManageDialog({
  customer,
  submitCallback,
  isOpen = false,
  setOpen,
}: StatusManageDialogProps) {
  const { toast } = useToast();
  const { id } = useParams();
  const auth = useAuthStore(state => state.auth);

  const {
    Dialog: StatusConfirmDialog,
    isOpen: isStatusConfirmDialogOpen,
    openDialog: openStatusConfirmDialog,
    closeDialog: closeStatusConfirmDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: WithdrawalFailDialog,
    isOpen: isWithdrawalFailDialogOpen,
    openDialog: openWithdrawalFailDialog,
    closeDialog: closeWithdrawalFailDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const statusForm = useForm<PutCustomersRequestBody>({
    resolver: zodResolver(statusField.formSchema()),
    defaultValues: statusField.DEFAULT_VALUES,
  });

  const watchFields = useWatch({ control: statusForm.control });

  const putCustomerStatus = useMutation({
    mutationKey: ['customers', 'normal', id],
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: PutCustomersRequestBody;
    }) => putCustomersAPI(id, params),
  });

  const {
    data: inprogressData,
    isLoading: isInprogressLoading,
    isFetching: isInprogressFetching,
  } = useQuery({
    queryKey: ['customers', 'check-inprogress', id],
    queryFn: () => {
      return getRecyclingRequestsCheckInprogressAPI(Number(id));
    },
    enabled: statusForm.watch('updatedStatus') === 'withdrawal',
  });

  const isFormValid = useMemo(() => {
    const statusChanged = watchFields.updatedStatus !== customer?.status;
    if (watchFields.updatedStatus === 'normal') {
      return statusChanged;
    }
    const updateReasonRequired = watchFields.updateReason !== '';
    return statusChanged && updateReasonRequired;
  }, [watchFields, customer]);

  const handleStatusSubmit = (params: PutCustomersRequestBody) => {
    if (inprogressData?.id) {
      openWithdrawalFailDialog();
      return;
    }

    putCustomerStatus.mutate(
      {
        id: formatter.STRING_TO_NUM(id!),
        params,
      },
      {
        onSuccess: async () => {
          await submitCallback?.();
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (customer && auth && isOpen) {
      statusForm.setValue(statusField.formFields.nick.name, customer.nick);
      statusForm.setValue(
        statusField.formFields.updatedStatus.name,
        customer.status,
      );
      statusForm.setValue(statusField.formFields.updateReason.name, '');
      statusForm.setValue(statusField.formFields.administrator.name, auth.name);
    }
  }, [isOpen, auth, statusForm, customer]);

  return (
    <>
      <WithdrawalFailDialog
        title="탈퇴 처리 불가"
        open={isWithdrawalFailDialogOpen}
        onOpenChange={closeWithdrawalFailDialog}
      >
        <div className="whitespace-pre-wrap">
          탈퇴 처리를 진행하려는 회원이 수거 신청 중이에요
          <br />
          수거 신청이 있을 경우 탈퇴가 불가능해요
        </div>
      </WithdrawalFailDialog>
      <StatusConfirmDialog
        title={statusConfirm[statusForm.getValues('updatedStatus')].title}
        open={isStatusConfirmDialogOpen}
        onOpenChange={closeStatusConfirmDialog}
        onOk={() => handleStatusSubmit(statusForm.getValues())}
        okButtonProps={{
          isLoading: putCustomerStatus.isPending,
        }}
      >
        {statusConfirm[statusForm.getValues('updatedStatus')].content}
      </StatusConfirmDialog>
      <ConfirmDialog
        title="회원 상태 관리"
        open={isOpen}
        onOpenChange={setOpen}
        onOk={() => openStatusConfirmDialog()}
        okButtonProps={{
          disabled: !isFormValid || isInprogressLoading || isInprogressFetching,
        }}
        isLoading={putCustomerStatus.isPending}
      >
        <Form {...statusForm}>
          <form className="flex flex-col gap-[24px]">
            <FormField
              control={statusForm.control}
              name={statusField.formFields.nick.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={statusField.formFields.nick.label}
                    labelSize="M"
                    name={statusField.formFields.nick.name}
                    type="text"
                    control={statusForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
            <FormField
              control={statusForm.control}
              name={statusField.formFields.updatedStatus.name}
              render={({ fieldState: { error } }) => {
                return (
                  <FormItem
                    label={statusField.formFields.updatedStatus.label}
                    labelSize="M"
                    name={statusField.formFields.updatedStatus.name}
                    type="select"
                    control={statusForm.control}
                    direction="vertical"
                    optionList={statusField.updatedStatusOptions.map(
                      item => item,
                    )}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
            {watchFields.updatedStatus !== 'normal' && (
              <FormField
                control={statusForm.control}
                name={statusField.formFields.updateReason.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormItem
                      label={statusField.formFields.updateReason.label}
                      labelSize="M"
                      name={statusField.formFields.updateReason.name}
                      type="textArea"
                      control={statusForm.control}
                      direction="vertical"
                      errorMessage={error?.message}
                      rules={{
                        required: true,
                      }}
                      inputProps={{
                        maxLength: 200,
                        className: 'h-[140px]',
                        placeholder:
                          statusField.formFields.updateReason.placeholder,
                        value: field.value,
                        onChange: e => field.onChange(e),
                      }}
                    />
                  );
                }}
              />
            )}

            <FormField
              control={statusForm.control}
              name={statusField.formFields.administrator.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={statusField.formFields.administrator.label}
                    labelSize="M"
                    name={statusField.formFields.administrator.name}
                    type="text"
                    control={statusForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
          </form>
        </Form>
      </ConfirmDialog>
    </>
  );
}

export default React.memo(StatusManageDialog);
